import { useDataTableUsingUrl } from '@juvare/common-data-table-ui/DataTable';
import { useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { launchBulkAction } from '@juvare/platform-sdk-react/ui/features/bulk-actions';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { Box, Typography } from '@mui/material';
import React from 'react';
// eslint-disable-next-line no-restricted-imports
import { archiveAlertsBulkActionType, deleteAlertsBulkActionType, } from '../../../../../backend/features/alerts/bulk-actions';
import alertImage from '../../../../assets/alertImage.svg';
import { showAlertNotificationRowDialog } from '../../../../components/AlertNotificationRowDialog';
import { useAlertUiTranslation } from '../../../../hooks';
import { getAlertBulkActionParams } from '../../../../utils';
import { useAlertCreateButton, useAlertRefreshButton, useAlertsTable } from '../../hooks';
import { AlertView } from '../AlertView';
import { AlertsList } from '../AlertsList';
export const AlertsListArchived = () => {
    var _a;
    const { t } = useAlertUiTranslation();
    const filterConfig = useFilterConfig((builder) => {
        const search = builder.quickSearchFilter({ dataPath: 'searchText', label: t('common.search') });
        const sentBy = builder.quickFilterSelect({
            dataPath: 'sentByMe',
            label: t('pages.tables.filters.sentBy'),
            values: [{ label: t('pages.tables.filters.anyone') }, { value: true, label: t('pages.tables.filters.me') }],
            widget: 'form.radio',
        });
        const notificationType = builder.quickFilterMultiSelect({
            dataPath: 'notificationType',
            label: t('pages.tables.filters.notificationType'),
            valueProviderType: 'classifier.messageType',
            widget: 'form.checkboxList',
        });
        const sender = builder.quickFilterMultiSelect({
            dataPath: 'sender',
            label: t('pages.tables.filters.sender'),
            valueProviderType: 'alerts.owners',
            widget: 'form.multiSelect',
        });
        builder.allFiltersPanel({
            children: [
                search,
                builder.allFiltersWidget({
                    type: 'form.text',
                    key: 'subject',
                    label: t('common.subject'),
                    emptyAsNull: true,
                }),
                sender,
                sentBy,
                notificationType,
            ],
        });
        builder.allFiltersPanel({
            label: t('common.dateSent'),
            children: [
                builder.allFiltersWidget({
                    type: 'form.dateTimePicker',
                    key: 'dateSentFrom',
                    dateLabel: t('common.fromDate'),
                    timeLabel: t('common.fromTime'),
                }),
                builder.allFiltersWidget({
                    type: 'form.dateTimePicker',
                    key: 'dateSentTo',
                    dateLabel: t('common.toDate'),
                    timeLabel: t('common.toTime'),
                }),
            ],
        });
    }, [t]);
    const handleRowClickDialog = (alertRowId) => {
        showAlertNotificationRowDialog({
            type: 'alert',
            onClearActiveRow: clearActiveRow,
            onReload: reload,
            label: t('common.backToNotifications'),
            rowId: alertRowId,
        });
    };
    const staticFilterData = { statuses: ['archived'] };
    const { dataTableProps, appliedFilterData, clearActiveRow, reload } = useAlertsTable({
        useDataTableFunction: useDataTableUsingUrl,
        staticFilterData: staticFilterData,
        filterConfig,
        defaultSortColumnKey: 'sendDateTime', // what happens to failed alerts that do not have sendDateTime,
        handleRowClickDialog,
    });
    const selectedAlertIds = (_a = dataTableProps.selectedRowIds) !== null && _a !== void 0 ? _a : [];
    const handleDelete = () => {
        const count = selectedAlertIds.length > 0 ? selectedAlertIds.length : dataTableProps.filteredRowCount;
        showConfirmDialog({
            title: t('deleteArchivedAlertsDialog.title', { count }),
            content: t('deleteArchivedAlertsDialog.message', { count }),
            confirmLabel: t('common.delete'),
            onConfirm: () => {
                launchBulkAction({
                    type: deleteAlertsBulkActionType,
                    title: t('deleteArchivedAlertsDialog.progress', { count }),
                    params: getAlertBulkActionParams(dataTableProps, appliedFilterData, staticFilterData),
                    onClose: () => {
                        var _a;
                        clearActiveRow();
                        (_a = dataTableProps.onDeselectAllRows) === null || _a === void 0 ? void 0 : _a.call(dataTableProps);
                        reload();
                    },
                });
            },
        });
    };
    const handleRestore = () => {
        const count = selectedAlertIds.length > 0 ? selectedAlertIds.length : dataTableProps.filteredRowCount;
        showConfirmDialog({
            title: t('restoreArchivedAlertsDialog.title', { count }),
            content: t('restoreArchivedAlertsDialog.message', { count, page: 'Sent' }),
            confirmLabel: t('common.restore'),
            onConfirm: () => {
                launchBulkAction({
                    type: archiveAlertsBulkActionType, // TODO currently Alert Service backend does not support separate "restore" action, calling archive just toggles archived state
                    title: t('restoreArchivedAlertsDialog.progress', { count }),
                    params: getAlertBulkActionParams(dataTableProps, appliedFilterData, staticFilterData),
                    onClose: () => {
                        var _a;
                        clearActiveRow();
                        (_a = dataTableProps.onDeselectAllRows) === null || _a === void 0 ? void 0 : _a.call(dataTableProps);
                        reload();
                    },
                });
            },
        });
    };
    const actions = [
        {
            icon: React.createElement(DeleteOutlinedIcon, null),
            label: t('common.delete'),
            onClick: handleDelete,
        },
        {
            icon: React.createElement(UnarchiveOutlinedIcon, null),
            label: t('common.restore'),
            onClick: handleRestore,
        },
    ];
    const mainAction = useAlertCreateButton();
    const secondaryAction = useAlertRefreshButton(() => reload());
    return (React.createElement(AlertsList, Object.assign({}, dataTableProps, { title: t('pages.tables.tabs.archived'), noAlertsLabel: t('common.thereAreNoArchivedAlerts'), localActions: actions, mainAction: mainAction, secondaryAction: secondaryAction }), dataTableProps.activeRowId ? (React.createElement(AlertView, { id: dataTableProps.activeRowId, onReload: (clearActiveAlert) => {
            if (clearActiveAlert) {
                clearActiveRow();
            }
            reload();
        } })) : (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, px: 2, py: '180px', alignItems: 'center' } },
        React.createElement("img", { src: alertImage, alt: '' }),
        React.createElement(Typography, { variant: 'subtitle1' }, t('common.countArchivedAlertsSelected', { count: selectedAlertIds.length }))))));
};
